import styles from "../../Styles/Knowledge Center/EntryDetails.module.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faArrowRight,
  faArrowLeft,
  faArrowRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Toast from "../../Components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import gearsGif from "../../Assets/gears.gif";

export const EntryDetails = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [topicName, setTopicName] = useState("");
  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const navigate = useNavigate();
  const { mainCategoryId, subCategoryId, topicId, entryId } = useParams();

  useEffect(() => {
    const getEntries = async () => {
      try {
        if (!mainCategoryId || !subCategoryId || !topicId || !entryId) {
          setEntries([]);
          return;
        }

        setLoading(true);
        const token = Cookies.get("adminToken");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/admin/entries/${mainCategoryId}/${subCategoryId}/${topicId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        const data = response.data?.data;
        const list = data?.entries || [];

        const targetEntry = list.find((entry) => entry._id === entryId);
        const groupTitle = targetEntry ? targetEntry.title : null;
        
        // Check if entry is in draft status
        const entryIsDraft = targetEntry ? targetEntry.status === "draft" : false;
        setIsDraft(entryIsDraft);

        const groupedEntries = groupTitle
          ? list.filter((entry) => entry.title === groupTitle)
          : [];

        const sharedImageEntry = groupedEntries.find((entry) => entry.image);
        if (sharedImageEntry && sharedImageEntry.image) {
          setImage(sharedImageEntry.image);
        } else {
          setImage("");
        }

        const difficulties = ["Beginner", "Moderate", "Advance"];

        const combined = {
          Beginner: { body: null, footer: null },
          Moderate: { body: null, footer: null },
          Advance: { body: null, footer: null },
        };

        groupedEntries.forEach((entry) => {
          if (entry?.contentByDifficulty) {
            difficulties.forEach((d) => {
              const c = entry.contentByDifficulty?.[d];
              if (!combined[d].body && c?.body) combined[d].body = c.body;
              if (!combined[d].footer && c?.footer)
                combined[d].footer = c.footer;
            });
            return;
          }

          const d = entry?.difficulty;
          if (difficulties.includes(d)) {
            if (!combined[d].body && entry?.body) combined[d].body = entry.body;
            if (!combined[d].footer && entry?.footer)
              combined[d].footer = entry.footer;
          }
        });

        const normalizedEntries = difficulties
          .map((d) => ({
            _id: `${entryId}_${d}`,
            difficulty: d,
            body: combined[d].body || "",
            footer: combined[d].footer || "",
          }))
          .filter((e) => e.body || e.footer);

        setEntries(normalizedEntries);
        if (groupTitle) {
          setTitle(groupTitle);
        }
        if (data?.topic?.name) {
          setTopicName(data.topic.name);
        }
      } catch (error) {
        console.error("entry details fetching error", error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to load entry details",
        );
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    getEntries();
  }, [mainCategoryId, subCategoryId, topicId, entryId]);

  const getImageUrl = (image) => {
    if (!image) {
      return null;
    }
    return `${process.env.REACT_APP_IMAGE_URL}/${image}`;
  };

  const handlePublish = async () => {
    if (!mainCategoryId || !subCategoryId || !topicId || !entryId) {
      return;
    }
    try {
      setPublishing(true);
      const token = Cookies.get("adminToken");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/admin/entry/${mainCategoryId}/${subCategoryId}/${topicId}/${entryId}/publish`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      toast.success("Blog published successfully");
      setTimeout(() => {
        navigate(`/admin/category/${mainCategoryId}`);
      }, 800);
    } catch (error) {
      console.error("publish blog error", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to publish blog",
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleLogout = () => {
    Cookies.remove("adminToken");
    navigate("/admin/signin");
  };

  return (
    <>
      <Toast />
      <div className={styles.pageContainer}>
        {/* Simple Header */}
        <header className={styles.simpleHeader}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <button
                className={styles.backBtn}
                onClick={() => navigate(`/admin/category/${mainCategoryId}`)}
                title="Back to Knowledge Center"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back to Knowledge Center</span>
              </button>
            </div>
            <div className={styles.headerCenter}>
              <div className={styles.titleRow}>
                <h1 className={styles.pageTitle}>
                  Preview <FontAwesomeIcon icon={faArrowRight} /> {title}
                </h1>
                {isDraft && (
                  <span className={styles.draftBadge}>Draft</span>
                )}
              </div>
              {topicName && (
                <p className={styles.topicSubtext}>
                  Viewing blog in <strong>{topicName}</strong> topic
                </p>
              )}
            </div>
            <div className={styles.headerRight}>
              <button
                className={styles.logoutBtn}
                onClick={handleLogout}
                title="Logout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className={styles.mainContentArea}>
          {loading && (
            <div className={styles.loadingOverlay}>
              <img
                src={gearsGif}
                alt="Loading"
                style={{ width: 40, height: 40 }}
              />
            </div>
          )}
          
          <div className={styles.contentContainer}>

              {getImageUrl(image) && (
                <div className={styles.imageContainer}>
                  <img
                    src={getImageUrl(image)}
                    alt="Entry"
                    className={styles.entryImage}
                  />
                </div>
              )}

              {!loading && entries.length === 0 && (
                <p className={styles.emptyMessage}>No entry details available.</p>
              )}

              {!loading &&
                entries.length > 0 &&
                entries.map((entry) => (
                  <div key={entry._id} className={styles.difficultyCard}>
                    <div className={styles.difficultyHeader}>
                      <span className={styles.difficultyTitle}>
                        {entry.difficulty || "-"}
                      </span>
                      <span className={styles.difficultyTag}>Difficulty</span>
                    </div>
                    <div className={styles.contentSection}>
                      <h6 className={styles.sectionTitle}>Body</h6>
                      <div
                        className={styles.contentDisplay}
                        dangerouslySetInnerHTML={{ __html: entry.body || "" }}
                      />
                    </div>
                    {entry.footer && (
                      <div className={styles.contentSection}>
                        <h6 className={styles.sectionTitle}>Footer</h6>
                        <div
                          className={styles.contentDisplay}
                          dangerouslySetInnerHTML={{
                            __html: entry.footer || "",
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.backToEntriesBtn}
                  onClick={() => navigate(`/admin/category/${mainCategoryId}`)}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Back to Knowledge Center
                </button>
                
                {isDraft && (
                  <>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() =>
                        navigate(
                          `/admin/topic-entries/${mainCategoryId}/${subCategoryId}/${topicId}/edit/${entryId}`
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      Back to Edit
                    </button>
                    
                    <button
                      type="button"
                      className={styles.publishBtn}
                      onClick={handlePublish}
                      disabled={publishing || loading}
                    >
                      {publishing ? "Publishing..." : "Publish Blog"}
                    </button>
                  </>
                )}
                
                {!isDraft && (
                  <div className={styles.publishedStatus}>
                    <span className={styles.publishedBadge}>✓ Published</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
